import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, Download, FileText, Briefcase, Clock, CalendarOff, Receipt, FolderOpen, FileSignature, UserCircle2, Printer, History, Package, AlertTriangle, Building2 } from "lucide-react";

const CATEGORIES = {
  contrat: { icon: FileSignature, color: "text-blue-600", bg: "bg-blue-50" },
  bulletin_paie: { icon: FileText, color: "text-emerald-600", bg: "bg-emerald-50" },
  dpae: { icon: UserCircle2, color: "text-amber-600", bg: "bg-amber-50" },
};

export default function RegistreSalaries() {
  const [list, setList] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [territoire, setTerritoire] = useState("");
  const [selected, setSelected] = useState(null); // dossier détail
  const [zipOpen, setZipOpen] = useState(false);
  const [zipEntreprise, setZipEntreprise] = useState("");
  const [zipMois, setZipMois] = useState(() => new Date().toISOString().slice(0, 7));
  const [zipLoading, setZipLoading] = useState(null); // 'fiches' | 'paie' | null

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, ent] = await Promise.all([
        api.get("/registre-salaries", { params: { search: search || undefined, territoire: territoire || undefined } }),
        api.get("/entreprises").catch(() => ({ data: [] })),
      ]);
      setList(data || []);
      setEntreprises(ent.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur chargement");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const downloadZipFiches = async (mode = "fiches") => {
    if (!zipEntreprise) { toast.error("Choisissez une entreprise"); return; }
    setZipLoading(mode);
    try {
      const url = mode === "paie"
        ? `/registre-salaries/entreprise/${zipEntreprise}/export-paie.zip`
        : `/registre-salaries/entreprise/${zipEntreprise}/fiches.zip`;
      const resp = await api.get(url, { responseType: "blob", params: { mois: zipMois } });
      const ent = entreprises.find(e => e.id === zipEntreprise);
      const dl = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = dl;
      const prefix = mode === "paie" ? "export-paie" : "fiches-salaries";
      a.download = `${prefix}-${(ent?.raison_sociale || "entreprise").replace(/\s+/g, "_")}-${zipMois}.zip`;
      a.click();
      URL.revokeObjectURL(dl);
      toast.success(mode === "paie" ? "Export paie téléchargé (fiches + bulletins + DPAE + LISEZ-MOI)" : "ZIP fiches téléchargé");
      setZipOpen(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Aucune fiche générée");
    } finally { setZipLoading(null); }
  };

  // Aperçu dynamique du contenu disponible pour l'entreprise sélectionnée
  const zipPreview = (() => {
    if (!zipEntreprise) return null;
    const members = list.filter(s => s.entreprise_id === zipEntreprise);
    const ent = entreprises.find(e => e.id === zipEntreprise);
    return { ent, count: members.length };
  })();

  const openDossier = async (sid) => {
    try {
      const { data } = await api.get(`/registre-salaries/${sid}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const downloadCSV = async () => {
    try {
      const resp = await api.get("/registre-salaries/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "registre-salaries-oscopge.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
  };

  const downloadFiche = async (sid, mois) => {
    try {
      const params = mois ? { mois } : {};
      const resp = await api.get(`/registre-salaries/${sid}/fiche.pdf`, { responseType: "blob", params });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Impossible de générer la fiche");
    }
  };

  return (
    <div className="space-y-6" data-testid="registre-salaries-page">
      <PageHeader
        title="Registre des salariés"
        subtitle="Dossier agrégé : contrats, missions, bulletins de paie, DPAE, feuilles de temps, absences, frais."
        icon={Users}
        actions={
          <div className="flex gap-2">
            <Button onClick={downloadCSV} variant="outline" data-testid="export-sal-csv"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
            <Button onClick={() => setZipOpen(true)} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="zip-fiches-btn">
              <FolderOpen className="h-4 w-4 mr-2" />ZIP fiches (entreprise)
            </Button>
          </div>
        }
      />

      <div className="flex gap-3 bg-white border border-slate-200 rounded-xl p-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Recherche nom, prénom, email, poste…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="sal-search"
          />
        </div>
        <Input placeholder="Territoire (facultatif)" value={territoire} onChange={(e) => setTerritoire(e.target.value)} className="w-56" />
        <Button variant="outline" onClick={load}>Rechercher</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Poste / Contrat</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Date entrée</TableHead>
                <TableHead className="text-center">Conv.</TableHead>
                <TableHead className="text-center">Missions</TableHead>
                <TableHead className="text-center">FT</TableHead>
                <TableHead className="text-center">Abs.</TableHead>
                <TableHead className="text-center">Frais</TableHead>
                <TableHead className="text-center">Docs</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400">Chargement…</TableCell></TableRow>
              ) : list.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-10 text-slate-400">Aucun salarié.</TableCell></TableRow>
              ) : list.map(s => (
                <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDossier(s.id)} data-testid={`sal-row-${s.id}`}>
                  <TableCell className="font-medium">
                    {s.prenom} {s.nom}
                    <div className="text-xs text-slate-500 font-normal">{s.email}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.poste || "—"}<br/>
                    <Badge variant="outline" className="text-[10px]">{s.type_contrat || "—"}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {s.raison_sociale || "—"}
                    {s.territoire && <><br/><span className="text-slate-400">{s.territoire}</span></>}
                  </TableCell>
                  <TableCell className="text-xs">{s.date_entree || "—"}</TableCell>
                  <TableCell className="text-center"><Count n={s.counts.conventions} /></TableCell>
                  <TableCell className="text-center"><Count n={s.counts.missions} /></TableCell>
                  <TableCell className="text-center"><Count n={s.counts.timesheets} /></TableCell>
                  <TableCell className="text-center"><Count n={s.counts.absences} /></TableCell>
                  <TableCell className="text-center"><Count n={s.counts.frais} /></TableCell>
                  <TableCell className="text-center"><Count n={s.counts.documents} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); downloadFiche(s.id); }} title="Fiche PDF (synthèse)" data-testid={`sal-fiche-${s.id}`}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDossier(s.id); }} data-testid={`sal-open-${s.id}`}>Dossier →</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {selected && <DossierSalarie dossier={selected} onDownloadFiche={downloadFiche} />}
        </DialogContent>
      </Dialog>

      {/* Dialog ZIP fiches par entreprise */}
      <Dialog open={zipOpen} onOpenChange={setZipOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-[#C5A059]" />Télécharger les fiches salariés
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Entreprise + mois côte à côte */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              <div className="sm:col-span-3">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Entreprise *</label>
                <Select value={zipEntreprise} onValueChange={setZipEntreprise}>
                  <SelectTrigger className="mt-1 h-11" data-testid="zip-entreprise-select">
                    <SelectValue placeholder="Sélectionner une entreprise…" />
                  </SelectTrigger>
                  <SelectContent>
                    {entreprises.map(e => (
                      <SelectItem key={e.id} value={e.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-medium">{e.raison_sociale}</span>
                          {e.territoire && <span className="text-xs text-slate-500">· {e.territoire}</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Mois</label>
                <input
                  type="month"
                  value={zipMois}
                  onChange={(e) => setZipMois(e.target.value)}
                  className="w-full mt-1 h-11 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40"
                  data-testid="zip-mois"
                />
              </div>
            </div>

            {/* Aperçu dynamique */}
            {zipPreview && (
              <div className="rounded-lg bg-[#FDF9F1] border border-[#C5A059]/40 p-3 text-sm flex items-start gap-3" data-testid="zip-preview">
                <Users className="h-5 w-5 text-[#A88846] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{zipPreview.ent?.raison_sociale}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    <strong>{zipPreview.count}</strong> salarié{zipPreview.count > 1 ? "s" : ""} dans cette entreprise · Période cible : <strong>{zipMois}</strong>
                  </div>
                  {zipPreview.count === 0 && (
                    <div className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />Aucun salarié rattaché — l'export sera vide.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Choix du format */}
            <div className="grid md:grid-cols-2 gap-3">
              {/* ZIP fiches seules */}
              <button
                onClick={() => downloadZipFiches("fiches")}
                disabled={!zipEntreprise || zipLoading !== null || (zipPreview && zipPreview.count === 0)}
                className="group text-left rounded-xl border-2 border-slate-200 hover:border-slate-400 bg-white p-4 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-200 relative"
                data-testid="zip-fiches-only-btn"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition">
                    <FileText className="h-5 w-5 text-slate-600" />
                  </div>
                  {zipLoading === "fiches" && <div className="text-xs text-slate-500 animate-pulse">Génération…</div>}
                </div>
                <div className="font-semibold text-slate-900">Fiches seules</div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Juste les fiches PDF du mois (identité, convention, heures, absences, frais).
                </p>
                <div className="mt-3 text-[11px] text-slate-400 font-mono">1 PDF par salarié</div>
              </button>

              {/* Export paie complet */}
              <button
                onClick={() => downloadZipFiches("paie")}
                disabled={!zipEntreprise || zipLoading !== null || (zipPreview && zipPreview.count === 0)}
                className="group text-left rounded-xl border-2 border-[#C5A059]/40 hover:border-[#C5A059] bg-gradient-to-br from-[#FDF9F1] to-white p-4 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#C5A059]/40 relative"
                data-testid="zip-paie-btn"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-[#C5A059] text-white flex items-center justify-center shadow-sm">
                    <Package className="h-5 w-5" />
                  </div>
                  <Badge className="bg-[#A88846] text-white text-[10px] uppercase tracking-wide">Recommandé</Badge>
                </div>
                <div className="font-semibold text-slate-900">Export paie complet</div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Fiches + bulletins de paie + DPAE + LISEZ-MOI récapitulatif. Prêt pour l'expert-comptable.
                </p>
                <div className="mt-3 text-[11px] text-[#A88846] font-mono flex items-center gap-2">
                  <span>📂 /fiches/</span>
                  <span>📂 /bulletins-paie/</span>
                  <span>📂 /dpae/</span>
                </div>
                {zipLoading === "paie" && <div className="absolute inset-0 rounded-xl bg-white/80 flex items-center justify-center text-sm font-semibold text-[#A88846]">Génération en cours…</div>}
              </button>
            </div>

            <div className="text-[11px] text-slate-400 border-t pt-3 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Les DPAE manquantes sont listées en fin du fichier LISEZ-MOI. Déposez-les dans la <strong>GED</strong> (catégorie « DPAE », case « Lier au salarié ») pour les intégrer au prochain export.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZipOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Count({ n }) {
  if (!n) return <span className="text-slate-300">0</span>;
  return <span className="inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">{n}</span>;
}

function DossierSalarie({ dossier, onDownloadFiche }) {
  const s = dossier.salarie;
  const a = dossier.agregats;
  const ent = dossier.entreprise;
  const [moisFiche, setMoisFiche] = useState(() => new Date().toISOString().slice(0, 7));

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#C5A059] to-[#A88846] text-white font-semibold flex items-center justify-center">
            {s.prenom?.[0]}{s.nom?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div>{s.prenom} {s.nom}</div>
            <div className="text-xs font-normal text-slate-500">{s.poste || "—"} · {s.type_contrat || "—"} · chez {ent?.raison_sociale || "—"} ({ent?.territoire || "—"})</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <input type="month" value={moisFiche} onChange={(e) => setMoisFiche(e.target.value)} className="text-xs rounded border border-slate-200 px-2 py-1 bg-white" data-testid="dossier-mois-filter" />
            <Button size="sm" variant="outline" onClick={() => onDownloadFiche?.(s.id, moisFiche)} data-testid="dossier-fiche-mois"><Printer className="h-3.5 w-3.5 mr-1" />Fiche mois</Button>
            <Button size="sm" className="bg-[#C5A059] hover:bg-[#A88846]" onClick={() => onDownloadFiche?.(s.id)} data-testid="dossier-fiche-global"><Printer className="h-3.5 w-3.5 mr-1" />Fiche globale</Button>
          </div>
        </DialogTitle>
      </DialogHeader>

      {/* Agrégats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
        <KpiMini label="Conventions" value={a.conventions} />
        <KpiMini label="Missions" value={`${a.missions_actives}/${a.missions}`} sub="actives/total" />
        <KpiMini label="Heures" value={`${a.heures_validees}h`} sub={`/ ${a.heures_total}h`} />
        <KpiMini label="Absences" value={`${a.absences_jours}j`} sub={`${a.absences_count} entrées`} />
        <KpiMini label="Frais" value={`${a.frais_total.toFixed(2)}€`} sub={`${a.frais_count} notes`} />
        <KpiMini label="Documents" value={a.contrats + a.bulletins_paie + a.dpae + a.documents} sub={`${a.contrats} contrats · ${a.bulletins_paie} paies · ${a.dpae} DPAE`} />
      </div>

      {dossier.convention_active && (
        <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
          <strong>Convention active :</strong> {dossier.convention_active.titre} · {dossier.convention_active.date_debut} → {dossier.convention_active.date_fin} · {dossier.convention_active.tarif_horaire || 0} €/h
        </div>
      )}

      <Tabs defaultValue="contrats">
        <TabsList className="grid grid-cols-4 md:grid-cols-8 gap-1">
          <TabsTrigger value="contrats"><FileSignature className="h-3.5 w-3.5 mr-1" />Contrats</TabsTrigger>
          <TabsTrigger value="paie"><FileText className="h-3.5 w-3.5 mr-1" />Paie</TabsTrigger>
          <TabsTrigger value="dpae"><UserCircle2 className="h-3.5 w-3.5 mr-1" />DPAE</TabsTrigger>
          <TabsTrigger value="missions"><Briefcase className="h-3.5 w-3.5 mr-1" />Missions</TabsTrigger>
          <TabsTrigger value="ft"><Clock className="h-3.5 w-3.5 mr-1" />Feuilles</TabsTrigger>
          <TabsTrigger value="absences"><CalendarOff className="h-3.5 w-3.5 mr-1" />Absences</TabsTrigger>
          <TabsTrigger value="frais"><Receipt className="h-3.5 w-3.5 mr-1" />Frais</TabsTrigger>
          <TabsTrigger value="audit"><History className="h-3.5 w-3.5 mr-1" />Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="contrats">
          <SectionList
            items={[...dossier.conventions.map(c => ({ ...c, _type: "convention" })), ...dossier.contrats.map(c => ({ ...c, _type: "doc" }))]}
            empty="Aucun contrat enregistré."
            render={(it) => it._type === "convention" ? (
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded border border-slate-200">
                <div>
                  <strong>{it.titre}</strong>
                  <div className="text-xs text-slate-500">{it.date_debut} → {it.date_fin} · {it.tarif_horaire || 0} €/h</div>
                </div>
                <Badge className={it.statut === "signee" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}>
                  {it.statut} {it.signatures_roles ? `(${it.signatures_roles.length}/3)` : ""}
                </Badge>
              </div>
            ) : <DocRow doc={it} />}
          />
        </TabsContent>

        <TabsContent value="paie">
          <SectionList items={dossier.bulletins_paie} empty="Aucun bulletin de paie archivé." render={(d) => <DocRow doc={d} />} />
        </TabsContent>

        <TabsContent value="dpae">
          <SectionList items={dossier.dpae} empty="Aucune DPAE archivée." render={(d) => <DocRow doc={d} />} />
        </TabsContent>

        <TabsContent value="missions">
          <SectionList items={dossier.missions} empty="Aucune mission." render={(m) => (
            <div className="flex justify-between p-3 bg-slate-50 rounded border border-slate-200">
              <div>
                <strong>{m.libelle}</strong>
                <div className="text-xs text-slate-500">{m.date_debut} → {m.date_fin} · {m.lieu || "—"}</div>
              </div>
              <Badge variant="outline">{m.statut}</Badge>
            </div>
          )} />
        </TabsContent>

        <TabsContent value="ft">
          <SectionList items={dossier.timesheets} empty="Aucune feuille de temps." render={(t) => (
            <div className="flex justify-between p-2 border-b border-slate-100 text-sm">
              <span>{t.date}</span>
              <span>{t.heures_total || 0}h</span>
              <Badge variant="outline" className="text-[10px]">{t.statut}</Badge>
            </div>
          )} />
        </TabsContent>

        <TabsContent value="absences">
          <SectionList items={dossier.absences} empty="Aucune absence." render={(ab) => (
            <div className="flex justify-between p-3 bg-slate-50 rounded border border-slate-200">
              <div>
                <strong>{ab.type}</strong>
                <div className="text-xs text-slate-500">{ab.date_debut} → {ab.date_fin} · {ab.nb_jours || 0} jours</div>
              </div>
              <Badge variant="outline">{ab.statut || "en_attente"}</Badge>
            </div>
          )} />
        </TabsContent>

        <TabsContent value="frais">
          <SectionList items={dossier.frais} empty="Aucun frais." render={(f) => (
            <div className="flex justify-between p-3 bg-slate-50 rounded border border-slate-200">
              <div>
                <strong>{f.libelle}</strong>
                <div className="text-xs text-slate-500">{f.date} · {f.categorie || "—"}</div>
              </div>
              <span className="font-semibold">{(f.montant || 0).toFixed(2)} €</span>
            </div>
          )} />
        </TabsContent>

        <TabsContent value="audit">
          {(dossier.audit_recent || []).length === 0 ? (
            <p className="text-sm text-slate-400 italic py-4 text-center">Aucune trace récente dans le journal d'audit pour ce salarié.</p>
          ) : (
            <ul className="divide-y divide-slate-100 mt-3">
              {dossier.audit_recent.map((log, i) => (
                <li key={log.id || i} className="py-2 flex items-start gap-3 text-sm" data-testid={`audit-log-${i}`}>
                  <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <History className="h-3.5 w-3.5 text-slate-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">{log.action}</Badge>
                      <span className="text-xs text-slate-400">{log.resource}{log.resource_id ? ` · ${String(log.resource_id).slice(0, 8)}` : ""}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {log.actor_email || "système"} · {(log.created_at || "").slice(0, 19).replace("T", " ")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-slate-400 mt-3 border-t pt-2">Traçabilité RGPD : 10 derniers événements liés à ce dossier.</p>
        </TabsContent>
      </Tabs>
    </>
  );
}

function KpiMini({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </div>
  );
}

function SectionList({ items, empty, render }) {
  if (!items || items.length === 0) return <p className="text-sm text-slate-400 italic py-4 text-center">{empty}</p>;
  return <div className="space-y-2 mt-3">{items.map((it, i) => <div key={it.id || i}>{render(it)}</div>)}</div>;
}

function DocRow({ doc }) {
  return (
    <div className="flex justify-between items-center p-3 bg-slate-50 rounded border border-slate-200">
      <div className="flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-slate-400" />
        <div>
          <strong className="text-sm">{doc.titre}</strong>
          <div className="text-xs text-slate-500">{doc.categorie} · v{doc.version || 1} · {doc.created_at?.slice(0, 10)}</div>
        </div>
      </div>
      <a href={`${API}/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">Télécharger</a>
    </div>
  );
}
